import React, { useContext, useMemo, useRef } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import {
  ImportSourceArgsGroupInput,
  LinkedInSearchContentType,
  LinkedInSearchSortType,
  LinkedInSearchType,
  PlanImporterType,
  SocialProfileFields_LinkedInSocialProfile_Fragment
} from '../../../../models/graphql';
import { SelectItem, SelectItemTemplate, SelectViewProps } from '../../../../components/forms/Select/Select.view';
import { SelectProfileItemTemplate } from '../SelectTemplate';
import { Select } from '../../../../components/forms/Select';
import { SelectProfileTemplateViewProps } from '../SelectTemplate/SelectTemplate.view';
import { FormElementValueType } from '../../../../models/basic-objects';
import { ContentFilterSubFilterWrapper } from '../ContentFilter';
import { useDidMount } from '../../../../hooks/useDidMount';
import { SourceLevelAction } from '../SourceLevelAction';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import classNames from 'classnames';
import classes from '../ContentFilter/ContentFilter.module.scss';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { SearchErrorBox } from '../SearchErrorBox';
import { Input } from '../../../../components/forms/Input';
import { useDimensions } from '../../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';

export type LinkedInFilterBarProps = {
  formInstance: UseFormReturn;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  width: number;
};

export type ContentFilterLinkedInViewProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  linkedInProfiles: SocialProfileFields_LinkedInSocialProfile_Fragment[];
  submitFormHandler: (value: FormElementValueType) => void;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  hasPosts: boolean;
  showSaveSourceModal: boolean;
  setShowSaveSourceModal: (show: boolean) => void;
  getLinkedInInputArgs: () => ImportSourceArgsGroupInput;
  apolloErrorKey: string | undefined;
};

export const LinkedInFilterBar = (props: LinkedInFilterBarProps): JSX.Element => {
  const { formInstance, width } = props;
  const { loading } = useContext(FindContentContext);
  const { importType, isTrial } = useOrganizationPlan();
  const isDisabledFeature = importType !== PlanImporterType.ADVANCED_IMPORTER;
  const filterBarContainerClasses = classNames('grid-x', classes.FilterBarContainer);

  const filterDefaultWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large
  });
  const filterInputWrapperClasses = classNames('cell', classes.InputWrapper, classes.HasButtonSibling, {
    'auto margin-top-small-8': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperSmallFilterWidth]: width >= BreakpointsValuesEnum.large
  });
  const filterButtonWrapperClasses = classNames('cell', classes.FilterButtonContainer, {
    'shrink margin-top-small-8': width < BreakpointsValuesEnum.large
  });

  // In place of setting default values, we determine if no value is set and do so
  // Setting default values will break loading a saved source
  useDidMount(() => {
    const { contentType, sortType } = formInstance.getValues();
    if (!contentType) {
      formInstance.setValue('contentType', LinkedInSearchContentType.ALL);
    }
    if (!sortType) {
      formInstance.setValue('sortType', LinkedInSearchSortType.LATEST);
    }
  });

  return (
    <div className={filterBarContainerClasses}>
      <div className={filterDefaultWrapperClasses}>
        <Select
          items={[
            { value: LinkedInSearchContentType.ALL, label: 'All Content' },
            { value: LinkedInSearchContentType.PHOTOS, label: 'All Photos' },
            { value: LinkedInSearchContentType.LINKS, label: 'All Links' }
          ]}
          name={'contentType'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || isDisabledFeature}
        />
      </div>
      <div className={filterDefaultWrapperClasses}>
        <Select
          items={[
            { value: LinkedInSearchSortType.LATEST, label: 'Latest' },
            { value: LinkedInSearchSortType.MOST_COMMENTED, label: 'Most Commented' },
            { value: LinkedInSearchSortType.MOST_LIKED, label: 'Most Liked' }
          ]}
          name={'sortType'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || isDisabledFeature}
        />
      </div>
      <div className={filterInputWrapperClasses}>
        <Input
          formInstance={formInstance}
          name={'term'}
          placeholder={'Search within these posts'}
          rules={{}}
          size={'medium'}
          disabled={loading || isDisabledFeature}
          onKeyDown={e => {
            // ENTER key presses submits form
            if (e.key === 'Enter') {
              props.filterSearchHandler();
            }
          }}
        />
      </div>
      <div className={filterButtonWrapperClasses}>
        <Button
          type={'button'}
          variant={'hollow'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          disabled={loading || isDisabledFeature}
          expanded={true}
          action={() => props.filterSearchHandler()}>
          Apply
        </Button>
      </div>
      {(isTrial || isDisabledFeature) && (
        <div className={classNames('cell', classes.AdvancedFeatureContainer)}>
          <AdvancedFeature feature={AdvancedFeatures.filterFeed} />
        </div>
      )}
    </div>
  );
};

export const ContentFilterLinkedInView = (props: ContentFilterLinkedInViewProps): JSX.Element => {
  const {
    formInstance,
    linkedInProfiles,
    submitFormHandler,
    showFilters,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getLinkedInInputArgs,
    filterSearchHandler,
    apolloErrorKey,
    hasPosts
  } = props;
  const { loading, error, searchStep } = useContext(FindContentContext);

  const bodyRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(bodyRef);

  const linkedInProfileListItems = useMemo(() => {
    if (linkedInProfiles.length < 1) {
      return [{ label: 'No Business Profiles', value: 'NoBusinessProfiles' }];
    } else {
      return linkedInProfiles.map(p => ({ label: p.displayName, value: p.id }));
    }
  }, [linkedInProfiles]);

  const itemTemplate: SelectItemTemplate = (props: SelectViewProps, index: number, item: SelectItem) => {
    const selectProps: SelectProfileTemplateViewProps = {
      ...props,
      index,
      item,
      showDisplayName: true,
      iconSize: 'small',
      socialProfiles: linkedInProfiles
    };

    return <SelectProfileItemTemplate key={index} {...selectProps} />;
  };

  const typeListItems =
    linkedInProfiles.length > 0
      ? [{ label: 'My Feed', value: LinkedInSearchType.PROFILE }]
      : [{ label: 'No profile connected', value: -1 }];

  const typeValue = useWatch({ control: formInstance.control, name: 'type' });

  const defaultSearchWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large
  });

  const thirdSearchWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-12': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperDefaultMaxWidth]: width >= BreakpointsValuesEnum.large
  });

  return (
    <>
      <div className={defaultSearchWrapperClasses}>
        <Select
          label={'Search Type'}
          items={typeListItems}
          name={'type'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={true}
        />
      </div>
      {typeValue === LinkedInSearchType.PROFILE && (
        <div className={thirdSearchWrapperClasses}>
          <Select
            items={linkedInProfileListItems}
            name={LinkedInSearchType.PROFILE}
            rules={{}}
            size={'medium'}
            useFormInstance={formInstance}
            label={'Which Profile’s Feed?'}
            disabled={loading || linkedInProfiles.length === 0}
            itemTemplate={itemTemplate}
            chooseLabel={linkedInProfiles.length > 0 ? 'Choose a Profile' : undefined}
            hideDefaultSelectOption={true}
            action={(value, complete) => {
              complete(true);
              submitFormHandler(value);
            }}
          />
        </div>
      )}
      <ContentFilterSubFilterWrapper
        showFilters={
          showFilters && ((searchStep === SearchStepEnum.ONE && hasPosts) || searchStep > SearchStepEnum.ONE)
        }>
        <LinkedInFilterBar
          formInstance={formInstance}
          filterSearchHandler={filterSearchHandler}
          width={width}></LinkedInFilterBar>
      </ContentFilterSubFilterWrapper>
      {/* Only show if we have posts from the 1 step */}
      {((showFilters && !error) || (searchStep === SearchStepEnum.TWO && !error)) && (
        <SourceLevelAction
          formInstance={formInstance}
          setShowSaveSourceModal={setShowSaveSourceModal}
          showSaveSourceModal={showSaveSourceModal}
          getArgsHandler={getLinkedInInputArgs}
        />
      )}
      {error && !loading && (
        <div className={'cell'}>
          <SearchErrorBox apolloErrorKey={apolloErrorKey} filterSearchHandler={filterSearchHandler} />
        </div>
      )}
    </>
  );
};
